'use client';

import React from 'react';
import Link from 'next/link';
import { buttonVariants } from '@/components/ui/Button';
import { cn } from '@/lib/utils';
import { Mail, HelpCircle, Handshake, ExternalLink, ArrowRight } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { useIntersectionObserver } from '@/app/hooks/useIntersectionObserver';

export default function ContactContent() {
  const { t } = useTranslation();

  const { ref: heroRef, isVisible: heroVisible } = useIntersectionObserver();
  const { ref: cardsRef, isVisible: cardsVisible } = useIntersectionObserver();
  const { ref: ctaRef, isVisible: ctaVisible } = useIntersectionObserver();

  const contactCards = [
    {
      icon: <Mail className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.contact.info.email.title'),
      value: t('pageContent.contact.info.email.value'),
      description: t('pageContent.contact.info.email.description'),
      href: 'mailto:info@sendvc.app',
    },
    {
      icon: <HelpCircle className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.contact.info.support.title'),
      description: t('pageContent.contact.info.support.description'),
      href: null,
    },
    {
      icon: <Handshake className="h-8 w-8 text-primary-500" />,
      title: t('pageContent.contact.info.partnerships.title'),
      description: t('pageContent.contact.info.partnerships.description'),
      href: null,
    },
  ];

  return (
    <>
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-gray-50">
        <div className="container">
          <div ref={heroRef} className={`max-w-3xl mx-auto text-center fade-in-section ${heroVisible ? 'is-visible' : ''}`}>
            <span className="inline-block bg-primary-50 text-primary-600 px-4 py-2 rounded-full text-sm font-semibold mb-4">
              {t('pageContent.contact.hero.tagline')}
            </span>
            <h1 className="text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
              {t('pageContent.contact.hero.title')}
            </h1>
            {/*
              Promoted from <p> to <h2>: the outline previously went h1 -> h3 (the
              contact-method card titles) with nothing at level 2. Copy is the existing
              hero.subtitle key — no new string introduced.
              CSS-neutrality: globals.css applies var(--font-playfair) to every h1-h6,
              so the body font is pinned back on explicitly here, and the size/weight
              the <p> inherited are stated as classes. Renders pixel-identical.
            */}
            <h2 className="text-xl font-normal font-[family-name:var(--font-inter)] text-gray-600">
              {t('pageContent.contact.hero.subtitle')}
            </h2>
          </div>
        </div>
      </section>

      {/* Contact Info Cards */}
      <section className="py-16 md:py-20">
        <div className="container">
          <div ref={cardsRef} className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-5xl mx-auto">
            {contactCards.map((card, index) => (
              <div
                key={index}
                className={`bg-white rounded-xl shadow-sm border border-gray-100 p-8 hover:shadow-md transition-shadow hover-lift fade-in-section ${cardsVisible ? 'is-visible' : ''}`}
                style={{ transitionDelay: `${index * 0.15}s` }}
              >
                <div className="inline-flex items-center justify-center w-14 h-14 bg-primary-50 rounded-xl mb-6">
                  {card.icon}
                </div>
                <h3 className="text-xl font-bold text-gray-900 mb-3">{card.title}</h3>
                {card.value && card.href && (
                  <a
                    href={card.href}
                    className="inline-flex items-center text-primary-500 font-medium hover:text-primary-600 transition-colors mb-3"
                  >
                    {card.value}
                    <ExternalLink className="ml-1.5 h-4 w-4" />
                  </a>
                )}
                <p className="text-gray-600 leading-relaxed">{card.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      {/* CTA Section */}
      <section className="py-16 md:py-20 bg-gradient-to-r from-primary-500 to-primary-600 text-white">
        <div className="container">
          <div ref={ctaRef} className={`text-center max-w-2xl mx-auto fade-in-section ${ctaVisible ? 'is-visible' : ''}`}>
            <h2 className="text-3xl md:text-4xl font-bold mb-4">
              {t('pageContent.contact.cta.title')}
            </h2>
            <p className="text-xl text-primary-100 mb-8">
              {t('pageContent.contact.cta.subtitle')}
            </p>
            {/*
              The anchor is the interactive element, so it carries the button styling
              itself via buttonVariants() instead of wrapping a <button> (an <a> may
              not contain a <button>).
            */}
            <Link
              href="/#upload-section"
              className={cn(buttonVariants({ variant: 'secondary', size: 'lg' }), 'hover-scale')}
            >
              {t('pageContent.contact.cta.button')} <ArrowRight className="ml-2 h-4 w-4" />
            </Link>
          </div>
        </div>
      </section>
    </>
  );
}
